/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

export function SalesDetailedLedgerInfo({ formData }) {
  const { values, setFieldValue, containView } = formData;
  const {
    details = [],
    totalIncurred = { quantity: 0, amount: 0, taxAmount: 0, deductionAmount: 0 },
    netRevenue = 0,
    costOfGoodsSold = 0,
    grossProfit = 0
  } = values;

  const num = (v) => (v && v !== "" && !isNaN(v) ? parseFloat(v) : 0);
  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "0");

  const addRow = () => {
    const newRow = {
      bookingDate: "",
      documentNumber: "",
      documentDate: "",
      description: "",
      correspondingAccount: "",
      quantity: 0,
      unitPrice: 0,
      amount: 0,
      taxAmount: 0,
      deductionAmount: 0,
    };
    setFieldValue("details", [...details, newRow]);
  };

  const removeRow = (idx) => {
    setFieldValue("details", details.filter((_, i) => i !== idx));
  };

  // Auto Calculations
  useEffect(() => {
    let totalQ = 0, totalA = 0, totalT = 0, totalD = 0;

    const newDetails = details.map(d => {
      // 1. Auto calc amount for each row
      const rowAmount = num(d.quantity) * num(d.unitPrice);

      totalQ += num(d.quantity);
      totalA += rowAmount;
      totalT += num(d.taxAmount);
      totalD += num(d.deductionAmount);

      return { ...d, amount: rowAmount };
    });

    const newTotalIncurred = {
      quantity: totalQ,
      amount: totalA,
      taxAmount: totalT,
      deductionAmount: totalD,
    };

    // 2. Net Revenue
    const newNetRevenue = totalA - totalT - totalD;

    // 3. Gross Profit
    const newGrossProfit = newNetRevenue - num(costOfGoodsSold);

    const hasChanged =
      JSON.stringify(newDetails) !== JSON.stringify(details) ||
      JSON.stringify(newTotalIncurred) !== JSON.stringify(totalIncurred) ||
      newNetRevenue !== netRevenue ||
      newGrossProfit !== grossProfit;

    if (hasChanged) {
      setFieldValue("details", newDetails);
      setFieldValue("totalIncurred", newTotalIncurred);
      setFieldValue("netRevenue", newNetRevenue);
      setFieldValue("grossProfit", newGrossProfit);
    }
  }, [JSON.stringify(details.map(d => ({ q: d.quantity, p: d.unitPrice, t: d.taxAmount, de: d.deductionAmount }))), costOfGoodsSold]);

  const s = {
    container: { fontFamily: '"Times New Roman", Times, serif', color: "#000", padding: "20px", backgroundColor: "#fff", fontSize: "14px" },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    titleBox: { textAlign: "center", margin: "10px 0" },
    inputDotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: "14px", fontFamily: "inherit", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    inputDottedSmall: { border: "none", borderBottom: "1px dotted #000", outline: "none", width: "40px", fontSize: "14px", fontFamily: "inherit", textAlign: "center", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontWeight: "bold", verticalAlign: "middle", backgroundColor: "#f9f9f9" },
    td: { border: "1px solid #000", padding: 0, height: "35px", verticalAlign: "middle" },
    inputTable: { width: "100%", height: "100%", border: "none", outline: "none", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableRight: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    removeBtn: { border: "none", background: "transparent", color: "red", cursor: "pointer", fontWeight: "bold", fontSize: "16px" }
  };

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "60%" }}>
          <div>Đơn vị: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.unit} onChange={(e) => setFieldValue("unit", e.target.value)} /></div>
          <div>Địa chỉ: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.address} onChange={(e) => setFieldValue("address", e.target.value)} /></div>
        </div>
        <div style={{ textAlign: "center", width: "40%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S35-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "11px" }}>(Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={{ margin: 0, textTransform: "uppercase" }}>SỔ CHI TIẾT BÁN HÀNG</h2>
        <div style={{ marginTop: 10 }}>
          Tên sản phẩm (hàng hóa, dịch vụ, bất động sản đầu tư): <input disabled={containView} style={{ ...s.inputDotted, width: "350px", textAlign: "center" }} value={values.productName} onChange={(e) => setFieldValue("productName", e.target.value)} />
        </div>
        <div style={{ marginTop: 5 }}>
          Năm: <input disabled={containView} style={s.inputDottedSmall} value={values.fiscalYear} onChange={(e) => setFieldValue("fiscalYear", e.target.value)} />
        </div>
        <div style={{ marginTop: 5 }}>
          Quyển số: <input disabled={containView} style={s.inputDottedSmall} value={values.bookNumber} onChange={(e) => setFieldValue("bookNumber", e.target.value)} />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: "80px" }} rowSpan={2}>Ngày, tháng ghi sổ</th>
              <th style={s.th} colSpan={2}>Chứng từ</th>
              <th style={{ ...s.th, width: "200px" }} rowSpan={2}>Diễn giải</th>
              <th style={{ ...s.th, width: "60px" }} rowSpan={2}>TK đối ứng</th>
              <th style={s.th} colSpan={3}>Doanh thu</th>
              <th style={s.th} colSpan={2}>Các khoản tính trừ</th>
              {!containView && <th style={{ ...s.th, width: "40px" }} rowSpan={2}>#</th>}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "60px" }}>Số hiệu</th>
              <th style={{ ...s.th, width: "60px" }}>Ngày tháng</th>
              <th style={{ ...s.th, width: "60px" }}>Số lượng</th>
              <th style={{ ...s.th, width: "100px" }}>Đơn giá</th>
              <th style={{ ...s.th, width: "100px" }}>Thành tiền</th>
              <th style={{ ...s.th, width: "100px" }}>Thuế</th>
              <th style={{ ...s.th, width: "100px" }}>Giảm trừ</th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {(details || []).map((item, idx) => (
              <tr key={idx}>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.bookingDate} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].bookingDate = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentNumber} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].documentNumber = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentDate} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].documentDate = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTable} value={item.description} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].description = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.correspondingAccount} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].correspondingAccount = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.quantity} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].quantity = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.unitPrice} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].unitPrice = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={{ ...s.td, textAlign: "right", padding: "0 8px", fontWeight: "bold" }}>{fmt(item.amount)}</td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.taxAmount} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].taxAmount = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.deductionAmount} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].deductionAmount = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(idx)}>–</button>
                  </td>
                )}
              </tr>
            ))}

            {/* Total Incurred Row */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>Cộng số phát sinh</td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(totalIncurred.quantity)}</td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(totalIncurred.amount)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(totalIncurred.taxAmount)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(totalIncurred.deductionAmount)}</td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* Financial Rows */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td} colSpan={3}></td>
              <td style={{ ...s.td, padding: "0 8px" }} colSpan={2}>- Doanh thu thuần</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px", color: "blue" }}>{fmt(netRevenue)}</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td} colSpan={3}></td>
              <td style={{ ...s.td, padding: "0 8px" }} colSpan={2}>- Giá vốn hàng bán</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTableRight, fontWeight: "bold" }}
                  value={costOfGoodsSold}
                  onChange={(e) => setFieldValue("costOfGoodsSold", e.target.value)}
                />
              </td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>
            <tr style={{ fontWeight: "bold", backgroundColor: "#fafafa" }}>
              <td style={s.td} colSpan={3}></td>
              <td style={{ ...s.td, padding: "0 8px" }} colSpan={2}>- Lãi gộp</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px", color: "green" }}>{fmt(grossProfit)}</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>

        {!containView && (
          <SoftBox mt={2}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={addRow}>+ Thêm dòng phát sinh</SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "25px" }}>
        <div style={{ marginBottom: "5px" }}>- Sổ này có <input disabled={containView} style={s.inputDottedSmall} value={values.totalPages} onChange={(e) => setFieldValue("totalPages", e.target.value)} /> trang, đánh số từ trang 01 đến trang...</div>
        <div>- Ngày mở sổ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px" }} value={values.openingDate} onChange={(e) => setFieldValue("openingDate", e.target.value)} /></div>
      </div>

      {/* <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", textAlign: "center" }}>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Người ghi sổ</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.ledgerClerk} onChange={(e) => setFieldValue("ledgerClerk", e.target.value)} /></SoftBox>
        </div>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Kế toán trưởng</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.chiefAccountant} onChange={(e) => setFieldValue("chiefAccountant", e.target.value)} /></SoftBox>
        </div>
        <div style={{ width: "40%" }}>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "5px" }}>Ngày <input disabled={containView} style={s.inputDottedSmall} value={values.signingDay} onChange={(e) => setFieldValue("signingDay", e.target.value)} /> tháng <input disabled={containView} style={s.inputDottedSmall} value={values.signingMonth} onChange={(e) => setFieldValue("signingMonth", e.target.value)} /> năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "50px" }} value={values.signingYear} onChange={(e) => setFieldValue("signingYear", e.target.value)} /></div>
          <b style={{ textTransform: "none" }}>Người đại diện theo pháp luật</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên, đóng dấu)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.legalRepresentative} onChange={(e) => setFieldValue("legalRepresentative", e.target.value)} /></SoftBox>
        </div>
      </div> */}
    </SoftBox>
  );
}
